import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  ArrowLeft,
  Wallet,
  ArrowRightLeft,
  Landmark,
  Plus,
  Minus,
  CheckCircle2,
  AlertTriangle,
  Info,
  ChevronRight,
  Loader2,
  Phone,
  Home,
  Gift,
  QrCode,
  History,
  User,
  Check
} from 'lucide-react';
import axios from '../config/AxiosConfig';
import { loadClient } from '../Redux/Actions/ClientActions';
import './Wallet.css';

const WalletPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { client, points: clientPointsFromRedux } = useSelector(state => state.clientReducer);

  // Local State
  const [walletBalance, setWalletBalance] = useState(0);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('history'); // 'history', 'convert', 'transfer'

  // Conversion State
  const [convDirection, setConvDirection] = useState('cash_to_points'); // 'cash_to_points' or 'points_to_cash'
  const [convAmount, setConvAmount] = useState('');
  const [convResult, setConvResult] = useState(0);
  const [convError, setConvError] = useState('');
  const [convSuccess, setConvSuccess] = useState('');

  // Bank Transfer State
  const [bankAmount, setBankAmount] = useState('');
  const [accountName, setAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [rib, setRib] = useState('');
  const [transferError, setTransferError] = useState('');
  const [transferSuccess, setTransferSuccess] = useState('');

  const CONVERSION_RATE = 100; // 1 DT = 100 points

  useEffect(() => {
    if (!client) {
      dispatch(loadClient());
    } else {
      fetchWalletData();
    }
  }, [client, dispatch]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/wallet/details');
      if (data.success) {
        setWalletBalance(data.walletBalance);
        setPointsBalance(data.points);
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error('Error fetching wallet details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Real-time Conversion Calculation
  useEffect(() => {
    const amountVal = parseFloat(convAmount);
    if (isNaN(amountVal) || amountVal <= 0) {
      setConvResult(0);
      setConvError('');
      return;
    }

    if (convDirection === 'cash_to_points') {
      const result = Math.floor(amountVal * CONVERSION_RATE);
      setConvResult(result);
      if (amountVal > walletBalance) {
        setConvError('Solde portefeuille insuffisant');
      } else {
        setConvError('');
      }
    } else {
      const result = parseFloat((amountVal / CONVERSION_RATE).toFixed(3));
      setConvResult(result);
      if (amountVal > pointsBalance) {
        setConvError('Solde de points insuffisant');
      } else {
        setConvError('');
      }
    }
  }, [convAmount, convDirection, walletBalance, pointsBalance]);

  const handleConvert = async (e) => {
    e.preventDefault();
    const amountVal = parseFloat(convAmount);
    if (isNaN(amountVal) || amountVal <= 0) {
      setConvError('Veuillez entrer un montant valide');
      return;
    }

    if (convDirection === 'cash_to_points' && amountVal > walletBalance) {
      setConvError('Solde portefeuille insuffisant');
      return;
    }

    if (convDirection === 'points_to_cash' && amountVal > pointsBalance) {
      setConvError('Solde de points insuffisant');
      return;
    }

    try {
      setActionLoading(true);
      setConvError('');
      setConvSuccess('');

      const endpoint = convDirection === 'cash_to_points'
        ? '/api/wallet/convert-to-points'
        : '/api/wallet/convert-from-points';

      const payload = convDirection === 'cash_to_points'
        ? { amount: amountVal }
        : { points: Math.floor(amountVal) };

      const { data } = await axios.post(endpoint, payload);

      if (data.success) {
        setConvSuccess(data.message);
        setConvAmount('');
        // Reload wallet details and points
        await fetchWalletData();
        dispatch(loadClient()); // sync redux state
      } else {
        setConvError(data.message || 'Erreur lors de la conversion');
      }
    } catch (err) {
      console.error(err);
      setConvError(err.response?.data?.message || 'Erreur de connexion au serveur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBankTransfer = async (e) => {
    e.preventDefault();
    const transferAmountVal = parseFloat(bankAmount);

    if (isNaN(transferAmountVal) || transferAmountVal < 20) {
      setTransferError('Le montant doit être de 20 DT minimum');
      return;
    }

    if (transferAmountVal > walletBalance) {
      setTransferError('Solde insuffisant pour ce virement');
      return;
    }

    if (!accountName || !bankName || !rib) {
      setTransferError('Toutes les informations bancaires sont requises');
      return;
    }

    const cleanRib = rib.replace(/\s+/g, '');
    if (cleanRib.length !== 24 || !/^\d+$/.test(cleanRib)) {
      setTransferError('Le RIB doit contenir exactement 24 chiffres');
      return;
    }

    try {
      setActionLoading(true);
      setTransferError('');
      setTransferSuccess('');

      const { data } = await axios.post('/api/wallet/transfer', {
        amount: transferAmountVal,
        accountName,
        bankName,
        rib: cleanRib
      });

      if (data.success) {
        setTransferSuccess(data.message);
        setBankAmount('');
        setAccountName('');
        setBankName('');
        setRib('');
        await fetchWalletData();
        dispatch(loadClient());
      } else {
        setTransferError(data.message || 'Erreur lors de la demande de virement');
      }
    } catch (err) {
      console.error(err);
      setTransferError(err.response?.data?.message || 'Erreur lors de la soumission de la demande');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRIB = (value) => {
    // Format RIB as: 12 345 678901234567890 12
    const v = value.replace(/\D/g, '').slice(0, 24);
    const matches = v.match(/^(\d{0,2})(\d{0,3})(\d{0,17})(\d{0,2})$/);
    if (!matches) return v;
    const parts = [matches[1], matches[2], matches[3], matches[4]].filter(Boolean);
    return parts.join(' ');
  };

  if (!client) return null;

  return (
    <div className="mobile-app-container">
      {/* Native Header */}
      <header className="mobile-native-header">
        <div className="header-main-content">
          <button className="p-2 hover:bg-secondary rounded-full" onClick={() => navigate('/my-points')}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="header-title-premium text-xl flex-1 text-center font-bold">Mon Portefeuille</h1>
          <div style={{ width: '40px' }}></div> {/* Spacer */}
        </div>
      </header>

      {/* Main Body */}
      <main className="mobile-main-feed">
        <div className="space-y-6">
          {/* Card Wallet */}
          <div className="wallet-card-container animate-in">
            <div className="wallet-bg-glow"></div>
            <div className="wallet-card-content flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div>
                  <p className="wallet-card-label">Solde Petite Monnaie</p>
                  <h2 className="wallet-card-balance">
                    {loading ? (
                      <span className="opacity-50">... DT</span>
                    ) : (
                      <>
                        {walletBalance.toFixed(3)}{' '}
                        <span className="text-xl font-normal opacity-85">DT</span>
                      </>
                    )}
                  </h2>
                </div>
                <div className="wallet-chip">
                  <Wallet size={32} className="text-white/80" />
                </div>
              </div>

              <div className="wallet-card-footer flex justify-between items-center pt-4 border-t border-white/10">
                <div>
                  <p className="wallet-card-sublabel">Points Fidélité</p>
                  <p className="wallet-card-points">
                    {loading ? '...' : pointsBalance.toLocaleString()} pts
                  </p>
                </div>
                <div className="wallet-card-tier bg-white/20 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white shadow-sm">
                  {client.level || 'Bronze'}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Info POS Banner */}
          <div className="pos-info-banner flex items-start gap-3 p-4 rounded-2xl border border-primary/10 bg-primary/5">
            <Phone size={24} className="text-primary shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-sm text-primary">Accumuler de la Monnaie</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Donnez simplement votre numéro de téléphone à la caisse du prestataire partenaire pour ajouter le reste de votre facture directement à ce portefeuille :
              </p>
              <p className="font-mono text-sm font-bold text-primary mt-2 bg-primary/10 py-1 px-3 rounded-lg inline-block">
                {client.phone}
              </p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="wallet-tabs-container">
            <button
              className={`wallet-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => { setActiveTab('history'); fetchWalletData(); }}
            >
              <History size={16} />
              <span>Activité</span>
            </button>
            <button
              className={`wallet-tab-btn ${activeTab === 'convert' ? 'active' : ''}`}
              onClick={() => setActiveTab('convert')}
            >
              <ArrowRightLeft size={16} />
              <span>Conversion</span>
            </button>
            <button
              className={`wallet-tab-btn ${activeTab === 'transfer' ? 'active' : ''}`}
              onClick={() => setActiveTab('transfer')}
            >
              <Landmark size={16} />
              <span>Virement</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="wallet-tab-content">
            {/* 1. History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-3 animate-in">
                <h3 className="section-title">Historique des transactions</h3>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 size={32} className="animate-spin text-primary" />
                  </div>
                ) : transactions.length > 0 ? (
                  <div className="compact-activity-list">
                    {transactions.map((tx) => {
                      const isCredit = ['add_change', 'convert_from_points'].includes(tx.type);
                      return (
                        <div key={tx._id} className="compact-activity-item">
                          <div className="activity-left">
                            <div className={`activity-mini-icon ${isCredit ? 'tx-earn' : 'tx-redeem'}`}>
                              {isCredit ? <Plus size={16} /> : <Minus size={16} />}
                            </div>
                            <div className="activity-details">
                              <p className="activity-name">{tx.description}</p>
                              <p className="activity-meta">{formatDate(tx.createdAt)}</p>
                              {tx.type === 'bank_transfer' && (
                                <span className={`status-badge ${tx.status}`}>
                                  {tx.status === 'pending' ? 'En attente' : tx.status === 'completed' ? 'Traité' : 'Rejeté'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={`activity-pts ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                            {isCredit ? '+' : '-'}{tx.amount.toFixed(3)} <span className="text-[10px] font-normal text-muted-foreground">DT</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground bg-secondary/20 rounded-2xl p-6 border border-dashed border-muted">
                    <Wallet size={36} className="mx-auto text-muted-foreground/60 mb-2" />
                    <p className="text-sm font-medium">Aucune transaction de portefeuille.</p>
                    <p className="text-xs text-muted-foreground mt-1">Vos dépôts de petite monnaie s'afficheront ici.</p>
                  </div>
                )}
              </div>
            )}

            {/* 2. Conversion Tab */}
            {activeTab === 'convert' && (
              <div className="space-y-4 animate-in">
                <h3 className="section-title">Convertir Solde & Points</h3>

                <div className="conversion-toggle-bar">
                  <button
                    type="button"
                    className={`toggle-direction-btn ${convDirection === 'cash_to_points' ? 'active' : ''}`}
                    onClick={() => {
                      setConvDirection('cash_to_points');
                      setConvAmount('');
                      setConvError('');
                      setConvSuccess('');
                    }}
                  >
                    Argent ➔ Points
                  </button>
                  <button
                    type="button"
                    className={`toggle-direction-btn ${convDirection === 'points_to_cash' ? 'active' : ''}`}
                    onClick={() => {
                      setConvDirection('points_to_cash');
                      setConvAmount('');
                      setConvError('');
                      setConvSuccess('');
                    }}
                  >
                    Points ➔ Argent
                  </button>
                </div>

                <form onSubmit={handleConvert} className="wallet-form">
                  <div className="form-group">
                    <label className="form-label">
                      {convDirection === 'cash_to_points'
                        ? 'Montant à convertir (DT)'
                        : 'Points à convertir'}
                    </label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        step={convDirection === 'cash_to_points' ? '0.050' : '1'}
                        min={convDirection === 'cash_to_points' ? '0.010' : '1'}
                        value={convAmount}
                        onChange={(e) => setConvAmount(e.target.value)}
                        placeholder={convDirection === 'cash_to_points' ? 'Ex: 2.500' : 'Ex: 200'}
                        className="form-input"
                        disabled={actionLoading}
                      />
                      <span className="input-suffix">
                        {convDirection === 'cash_to_points' ? 'DT' : 'points'}
                      </span>
                    </div>
                    <span className="form-hint">
                      {convDirection === 'cash_to_points'
                        ? `Disponible : ${walletBalance.toFixed(3)} DT`
                        : `Disponible : ${pointsBalance.toLocaleString()} points`}
                    </span>
                  </div>

                  {/* Realtime Result Preview */}
                  {convResult > 0 && !convError && (
                    <div className="conversion-preview-box">
                      <p className="preview-label">Vous obtiendrez environ :</p>
                      <p className="preview-value text-green-600">
                        {convDirection === 'cash_to_points'
                          ? `+${convResult.toLocaleString()} Points`
                          : `+${convResult.toFixed(3)} DT`}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Taux d'échange : 1.000 DT = 100 points
                      </p>
                    </div>
                  )}

                  {convError && (
                    <div className="error-alert flex items-center gap-2 p-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-xs font-medium">
                      <AlertTriangle size={16} />
                      <span>{convError}</span>
                    </div>
                  )}

                  {convSuccess && (
                    <div className="success-alert flex items-center gap-2 p-3 rounded-xl border border-green-200 bg-green-50 text-green-700 text-xs font-medium">
                      <CheckCircle2 size={16} />
                      <span>{convSuccess}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="submit-btn-premium mt-4 flex items-center justify-center gap-2"
                    disabled={actionLoading || !!convError || !convAmount}
                  >
                    {actionLoading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        <ArrowRightLeft size={18} />
                        <span>Convertir maintenant</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* 3. Transfer Tab */}
            {activeTab === 'transfer' && (
              <div className="space-y-4 animate-in">
                <h3 className="section-title">Virement sur compte bancaire</h3>

                {walletBalance < 20 ? (
                  <div className="transfer-locked-box text-center p-6 bg-secondary/10 border border-muted rounded-2xl space-y-3">
                    <Landmark size={40} className="mx-auto text-muted-foreground/60" />
                    <h4 className="font-bold text-sm">Virement bancaire indisponible</h4>
                    <p className="text-xs text-muted-foreground px-4">
                      Un solde minimum de <span className="font-bold text-primary">20.000 DT</span> est requis pour demander un virement vers votre compte bancaire.
                    </p>
                    <div className="pt-2">
                      <div className="text-xs text-muted-foreground mb-1">Votre solde actuel :</div>
                      <div className="text-lg font-bold text-slate-700">
                        {walletBalance.toFixed(3)} DT
                      </div>
                      <div className="w-full bg-slate-200 h-2 rounded-full mt-2 overflow-hidden max-w-[200px] mx-auto">
                        <div
                          className="bg-primary h-full rounded-full"
                          style={{ width: `${Math.min((walletBalance / 20) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleBankTransfer} className="wallet-form">
                    <p className="text-xs text-muted-foreground mb-4">
                      Transférez vos fonds vers n'importe quel compte bancaire en Tunisie (traitement sous 48h).
                    </p>

                    <div className="form-group">
                      <label className="form-label">Montant à transférer (DT)</label>
                      <div className="input-with-suffix">
                        <input
                          type="number"
                          step="1"
                          min="20"
                          max={walletBalance}
                          value={bankAmount}
                          onChange={(e) => setBankAmount(e.target.value)}
                          placeholder="Minimum 20 DT"
                          className="form-input"
                          required
                          disabled={actionLoading}
                        />
                        <span className="input-suffix">DT</span>
                      </div>
                      <span className="form-hint">
                        Max transférable : {walletBalance.toFixed(3)} DT
                      </span>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Nom complet du titulaire</label>
                      <input
                        type="text"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="Nom & Prénom"
                        className="form-input"
                        required
                        disabled={actionLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Nom de la banque</label>
                      <input
                        type="text"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder="Ex: BIAT, BNA, Attijari..."
                        className="form-input"
                        required
                        disabled={actionLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">RIB (24 chiffres)</label>
                      <input
                        type="text"
                        value={rib}
                        onChange={(e) => setRib(formatRIB(e.target.value))}
                        placeholder="Ex: 01 234 5678901234567890 12"
                        className="form-input font-mono"
                        required
                        disabled={actionLoading}
                      />
                      <span className="form-hint">RIB tunisien composé de 24 chiffres</span>
                    </div>

                    {transferError && (
                      <div className="error-alert flex items-center gap-2 p-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-xs font-medium">
                        <AlertTriangle size={16} />
                        <span>{transferError}</span>
                      </div>
                    )}

                    {transferSuccess && (
                      <div className="success-alert flex items-center gap-2 p-3 rounded-xl border border-green-200 bg-green-50 text-green-700 text-xs font-medium">
                        <CheckCircle2 size={16} />
                        <span>{transferSuccess}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="submit-btn-premium mt-4 flex items-center justify-center gap-2"
                      disabled={actionLoading || !bankAmount || !accountName || !bankName || !rib}
                    >
                      {actionLoading ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <>
                          <Landmark size={18} />
                          <span>Demander le transfert</span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Floating Navigation Bottom Bar */}
      <nav className="mobile-floating-nav">
        <button className="nav-btn" onClick={() => navigate('/my-points')}>
          <Home size={22} />
          <span>Accueil</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/rewards')}>
          <Gift size={22} />
          <span>Cadeaux</span>
        </button>
        <button className="nav-btn is-active">
          <div className="nav-active-dot"></div>
          <Wallet size={22} />
          <span>Wallet</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/history')}>
          <History size={22} />
          <span>Activités</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/profile')}>
          <User size={22} />
          <span>Profil</span>
        </button>
      </nav>
    </div>
  );
};

export default WalletPage;
